import React, { useState, useEffect, useCallback } from 'react'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import {
  FilmSlateIcon,
  EyeIcon,
  ClockIcon,
  CheckSquareIcon,
  SquareIcon,
  PlusIcon,
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
} from '@phosphor-icons/react'
import api from '../services/api'
import { toast } from 'sonner'
import AddProjectModal from '../components/AddProjectModal'

const COLUMNS = [
  {
    id: 'idea',
    label: 'Idea',
    color: '#FFD60A',
    bg: 'rgba(255,214,10,0.08)',
    border: 'rgba(255,214,10,0.2)',
  },
  {
    id: 'plan',
    label: 'Plan',
    color: '#5E5CE6',
    bg: 'rgba(94,92,230,0.08)',
    border: 'rgba(94,92,230,0.2)',
  },
  {
    id: 'record',
    label: 'Grabar',
    color: '#D42B1E',
    bg: 'rgba(255,59,48,0.08)',
    border: 'rgba(255,59,48,0.2)',
  },
  {
    id: 'edit',
    label: 'Editar',
    color: '#FF9F0A',
    bg: 'rgba(255,159,10,0.08)',
    border: 'rgba(255,159,10,0.2)',
  },
  {
    id: 'analyze',
    label: 'Analizar',
    color: '#007AFF',
    bg: 'rgba(0,122,255,0.08)',
    border: 'rgba(0,122,255,0.2)',
  },
  {
    id: 'publish',
    label: 'Publicar',
    color: '#32ADE6',
    bg: 'rgba(50,173,230,0.08)',
    border: 'rgba(50,173,230,0.2)',
  },
]

// Las seis etapas no caben a la vez en 1440: plegar las de los extremos a una
// tira vertical mantiene el pipeline completo visible sin perder ninguna etapa.
// Idea y Publicado son las que menos se tocan a diario — la una es bandeja de
// entrada, la otra es archivo — así que son las que arrancan plegadas.
const COLLAPSED_KEY = 'rdk.kanban.collapsed'
const DEFAULT_COLLAPSED = ['idea', 'publish']

const PLATFORM_COLORS = {
  YouTube: '#FF0000',
  Instagram: '#E1306C',
  TikTok: '#69C9D0',
  LinkedIn: '#0077B5',
}

function getChecklist(project) {
  const status = project.status || 'idea'
  return project.checklist?.[status] || []
}

function getProgress(project) {
  const items = getChecklist(project)
  if (!items.length) return 0
  const done = items.filter(i => i.done).length
  return Math.round((done / items.length) * 100)
}

function DraggableCard({ project, onChecklistToggle, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: project.id,
  })
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    borderRadius: 2,
  }
  const checklist = getChecklist(project)
  const progress = getProgress(project)
  const col = COLUMNS.find(c => c.id === project.status) || COLUMNS[0]

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`bg-ds-elevated border border-ds-border p-3 select-none hover:border-ds-border-mid transition-all duration-150 ${isDragging ? 'kanban-dragging' : ''}`}
      data-testid={`kanban-card-${project.id}`}
      onClick={e => {
        if (Math.abs(transform?.x || 0) < 5) onEdit(project)
      }}
    >
      {/* Title + Platform */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4
          className="text-ds-text text-sm font-medium leading-snug line-clamp-2 flex-1"
          style={{ fontFamily: 'Barlow, sans-serif' }}
        >
          {project.title}
        </h4>
        <span
          className="type-caption shrink-0 px-1.5 py-0.5"
          style={{
            color: PLATFORM_COLORS[project.platform] || '#E8E6E0',
            background: 'rgba(13,13,13,0.7)',
            borderRadius: 2,
          }}
        >
          {project.platform?.slice(0, 2)}
        </span>
      </div>

      {/* Tags */}
      {project.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {project.tags.slice(0, 2).map(t => (
            <span
              key={t}
              className="type-caption text-ds-muted bg-ds-surface px-1.5 py-0.5"
              style={{ borderRadius: 2 }}
            >
              #{t}
            </span>
          ))}
        </div>
      )}

      {/* Checklist */}
      {checklist.length > 0 && (
        <div className="space-y-1 mb-2">
          {checklist.map(item => (
            <div
              key={item.id}
              className="flex items-center gap-1.5 cursor-pointer"
              onClick={e => {
                e.stopPropagation()
                onChecklistToggle(project, item.id)
              }}
              data-testid={`checklist-item-${item.id}`}
            >
              {item.done ? (
                <CheckSquareIcon size={12} weight="fill" style={{ color: col.color }} />
              ) : (
                <SquareIcon size={12} className="text-[#4A4A4A]" />
              )}
              <span
                className={`text-[11px] leading-tight ${item.done ? 'line-through text-[#4A4A4A]' : 'text-[#A1A1A6]'}`}
              >
                {item.text}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Footer metrics + progress */}
      <div className="flex items-center justify-between">
        <div
          className="flex items-center gap-2.5 text-[11px] text-ds-muted"
          style={{ fontFamily: 'Barlow, sans-serif' }}
        >
          <span className="flex items-center gap-0.5">
            <ClockIcon size={10} />
            {project.duration_min}m
          </span>
          {project.estimated_views > 0 && (
            <span className="flex items-center gap-0.5">
              <EyeIcon size={10} />
              {project.estimated_views >= 1000
                ? `${(project.estimated_views / 1000).toFixed(1)}k`
                : project.estimated_views}
            </span>
          )}
        </div>
        {checklist.length > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-12 h-1.5 bg-[#2A2A2A] rounded-sharp overflow-hidden">
              <div
                className="h-full rounded-sharp transition-all"
                style={{
                  width: `${progress}%`,
                  background: col.color,
                }}
              />
            </div>
            <span className="type-caption text-ds-muted">{progress}%</span>
          </div>
        )}
      </div>
    </div>
  )
}

function DroppableColumn({ column, projects, onChecklistToggle, onEdit, activeId, onCollapse }) {
  const { isOver, setNodeRef } = useDroppable({ id: column.id })

  return (
    <div
      ref={setNodeRef}
      className="flex-1 min-w-[240px] max-w-[272px] flex flex-col transition-all duration-150"
      style={{
        background: isOver ? column.bg : '#141414',
        border: `1px solid ${isOver ? column.border : '#1E1E1E'}`,
        borderRadius: 2,
      }}
      data-testid={`kanban-column-${column.id}`}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-ds-border">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5" style={{ background: column.color, borderRadius: 1 }} />
          <span className="type-label text-ds-text">{column.label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="type-caption px-2 py-0.5"
            style={{ color: column.color, background: column.bg, borderRadius: 2 }}
          >
            {projects.length}
          </span>
          <button
            type="button"
            onClick={() => onCollapse(column.id)}
            className="text-ds-faint hover:text-ds-text transition-colors p-0.5"
            aria-label={`Plegar ${column.label}`}
            title={`Plegar ${column.label}`}
            data-testid={`kanban-collapse-${column.id}`}
          >
            <CaretDoubleLeftIcon size={13} weight="bold" />
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-2 p-2 min-h-[200px]">
        {projects.map(project => (
          <DraggableCard
            key={project.id}
            project={project}
            onChecklistToggle={onChecklistToggle}
            onEdit={onEdit}
            data-testid={`card-in-${column.id}-${project.id}`}
          />
        ))}
        {projects.length === 0 && (
          <div className="flex flex-col items-center justify-center h-24 text-ds-faint type-caption">
            <FilmSlateIcon size={22} className="mb-1.5" weight="duotone" />
            Arrastra aquí
          </div>
        )}
      </div>
    </div>
  )
}

function CollapsedColumn({ column, projects, onExpand }) {
  const { isOver, setNodeRef } = useDroppable({ id: column.id })

  // Arrastrar una tarjeta encima despliega la etapa, igual que el clic.
  useEffect(() => {
    if (isOver) onExpand(column.id)
  }, [isOver, column.id, onExpand])

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={() => onExpand(column.id)}
      aria-expanded={false}
      title={`Desplegar ${column.label} · ${projects.length}`}
      className="w-11 shrink-0 min-h-[220px] flex flex-col items-center gap-3 py-3 transition-all duration-150"
      style={{
        background: isOver ? column.bg : '#141414',
        border: `1px solid ${isOver ? column.border : '#1E1E1E'}`,
        borderRadius: 2,
      }}
      data-testid={`kanban-column-${column.id}-collapsed`}
    >
      <div className="w-1.5 h-1.5 shrink-0" style={{ background: column.color, borderRadius: 1 }} />
      <span
        className="type-caption px-1.5 py-0.5 shrink-0"
        style={{ color: column.color, background: column.bg, borderRadius: 2 }}
      >
        {projects.length}
      </span>
      <span
        className="type-label text-ds-muted whitespace-nowrap"
        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
      >
        {column.label}
      </span>
      <CaretDoubleRightIcon size={12} weight="bold" className="text-ds-faint shrink-0" />
    </button>
  )
}

export default function KanbanPage() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeProject, setActiveProject] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editProject, setEditProject] = useState(null)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSED_KEY)
      if (saved) return new Set(JSON.parse(saved))
    } catch {
      // localStorage bloqueado: se arranca con el plegado por defecto
    }
    return new Set(DEFAULT_COLLAPSED)
  })

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]))
    } catch {
      // sin persistencia el plegado dura lo que la sesión
    }
  }, [collapsed])

  const collapseColumn = useCallback(id => {
    setCollapsed(prev => (prev.has(id) ? prev : new Set(prev).add(id)))
  }, [])

  // Devuelve el estado anterior si no cambia nada: CollapsedColumn llama a esto
  // desde un efecto y una referencia nueva provocaría un bucle de renderizado.
  const expandColumn = useCallback(id => {
    setCollapsed(prev => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  )

  const fetchProjects = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/projects')
      setProjects(res.data)
    } catch {
      toast.error('Error al cargar vídeos')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  const handleDragStart = ({ active }) => {
    const proj = projects.find(p => p.id === active.id)
    setActiveProject(proj)
  }

  const handleDragEnd = async ({ active, over }) => {
    setActiveProject(null)
    if (!over) return
    const projectId = active.id
    const newStatus = over.id
    const project = projects.find(p => p.id === projectId)
    if (!project || project.status === newStatus) return

    // Optimistic update
    setProjects(prev => prev.map(p => (p.id === projectId ? { ...p, status: newStatus } : p)))
    try {
      await api.patch(`/projects/${projectId}/status`, {
        status: newStatus,
      })
      toast.success(`Movido a ${COLUMNS.find(c => c.id === newStatus)?.label}`)
    } catch {
      toast.error('Error al mover')
      setProjects(prev =>
        prev.map(p => (p.id === projectId ? { ...p, status: project.status } : p))
      )
    }
  }

  const handleChecklistToggle = async (project, itemId) => {
    const currentChecklist = project.checklist?.[project.status] || []
    const updatedChecklist = currentChecklist.map(item =>
      item.id === itemId ? { ...item, done: !item.done } : item
    )
    const newChecklist = {
      ...(project.checklist || {}),
      [project.status]: updatedChecklist,
    }

    // Optimistic update
    setProjects(prev =>
      prev.map(p => (p.id === project.id ? { ...p, checklist: newChecklist } : p))
    )
    try {
      await api.patch(`/projects/${project.id}/checklist`, {
        checklist: newChecklist,
      })
    } catch {
      toast.error('Error al actualizar checklist')
      fetchProjects()
    }
  }

  const handleEdit = project => {
    setEditProject(project)
    setModalOpen(true)
  }
  const handleProjectSaved = () => {
    fetchProjects()
    setModalOpen(false)
    setEditProject(null)
  }

  const getColumnProjects = columnId => projects.filter(p => p.status === columnId)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 lg:px-8 py-5 border-b border-ds-border shrink-0">
        <div>
          <h1 className="type-h2 text-ds-text text-2xl">Pipeline</h1>
          <p className="text-ds-muted text-sm mt-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {projects.length} vídeos · Arrastra para mover entre etapas
          </p>
        </div>
        <div className="flex items-center gap-2">
          {collapsed.size > 0 && (
            <button
              type="button"
              onClick={() => setCollapsed(new Set())}
              className="type-label text-ds-muted hover:text-ds-text transition-colors px-3 py-2"
              data-testid="kanban-expand-all"
            >
              Ver las {COLUMNS.length} etapas
            </button>
          )}
          <button
            onClick={() => {
              setEditProject(null)
              setModalOpen(true)
            }}
            className="btn-accent flex items-center gap-2"
            data-testid="kanban-add-btn"
          >
            <PlusIcon size={15} weight="bold" />
            Nueva Idea
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      {loading ? (
        <div className="flex gap-3 p-6 overflow-x-auto">
          {COLUMNS.map(col => (
            <div
              key={col.id}
              className={`h-48 bg-ds-surface animate-pulse border border-ds-border ${
                collapsed.has(col.id) ? 'w-11 shrink-0' : 'flex-1 min-w-[240px] max-w-[272px]'
              }`}
              style={{ borderRadius: 2 }}
            />
          ))}
        </div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div
            className="flex gap-3 p-6 overflow-x-auto flex-1 items-start"
            data-testid="kanban-board"
          >
            {COLUMNS.map(column =>
              collapsed.has(column.id) ? (
                <CollapsedColumn
                  key={column.id}
                  column={column}
                  projects={getColumnProjects(column.id)}
                  onExpand={expandColumn}
                />
              ) : (
                <DroppableColumn
                  key={column.id}
                  column={column}
                  projects={getColumnProjects(column.id)}
                  onChecklistToggle={handleChecklistToggle}
                  onEdit={handleEdit}
                  activeId={activeProject?.id}
                  onCollapse={collapseColumn}
                />
              )
            )}
          </div>
          <DragOverlay>
            {activeProject ? (
              <div
                className="bg-ds-elevated border border-ds-border-mid p-3 w-[272px] rotate-1 scale-102 opacity-90"
                style={{ borderRadius: 2 }}
              >
                <h4
                  className="text-ds-text text-sm font-medium"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  {activeProject.title}
                </h4>
                <p
                  className="text-ds-muted text-xs mt-1"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  {activeProject.platform}
                </p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <AddProjectModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setEditProject(null)
        }}
        onCreated={handleProjectSaved}
        editProject={editProject}
      />
    </div>
  )
}
